import { isEqual, omit } from 'lodash';
import { translateBlueprintUnderlay } from '@shared/helpers/design/blueprint-underlay.helpers';
import { normalizeDesignLocationAndPoints } from '@shared/helpers/design/design.helpers';
import { mutateStorey } from '@shared/helpers/design/mutators/storey.mutator';
import { removeEmptyStoreys } from '@shared/helpers/design/storey-prune.helpers';
import { assertUnreachable } from '@shared/helpers/function.helpers';
import { omitUndefined } from '@shared/helpers/object.helpers';
import { guards } from '@shared/helpers/typeguards.helpers';
import type {
  Building,
  BuildingDesign,
} from '@shared/models/design/building-design.model';
import type { BlueprintUnderlay } from '@shared/models/design/blueprint-underlay.model';
import { moveCoordinate } from '@shared/helpers/geometry/coordinates.helpers';
import type { Point2D } from '@shared/models/geometry.model';
import type { Coordinate } from '@shared/models/geography.model';
import type { Storey } from '@shared/models/design/storey.model';
import type { Project } from '@shared/models/project.model';
import { acquireDesignSaveHold } from './design-persist-lock.helpers';

const PERSISTED_DESIGN_FIELDS = [
  'location',
  'activities',
  'buildings',
  'blueprintUnderlays',
] as const satisfies readonly (keyof BuildingDesign)[];

type QueuedDesignState = {
  pendingCount: number;
  tail: BuildingDesign;
};

type SerializedDesignSnapshotPersistOptions<T> = {
  projectId: Project['id'];
  incoming: BuildingDesign;
  getLiveDesign: () => BuildingDesign;
  persist: (design: BuildingDesign) => Promise<T>;
};

const queuedDesignStateByProject = new Map<Project['id'], QueuedDesignState>();

/**
 * Orders rebase across overlapping design saves. Released after {@link persist}
 * has applied its synchronous update, not after enrich finishes.
 */
let designSnapshotTurn: Promise<void> = Promise.resolve();

const DESIGN_SNAPSHOT_SUPERSEDED =
  'Design update was cancelled because newer changes affected the same fields.';

/**
 * A queued design snapshot was dropped because a newer edit already changed
 * the same fields. This is a cancellation, not a failed save.
 */
export class DesignSnapshotSupersededError extends Error {
  constructor() {
    super(DESIGN_SNAPSHOT_SUPERSEDED);
    this.name = 'DesignSnapshotSupersededError';
  }
}

/**
 * True when {@link error} is a {@link DesignSnapshotSupersededError}.
 * Other rejections are save failures and must still surface.
 */
export const isDesignSnapshotSupersededError = (
  error: unknown,
): error is DesignSnapshotSupersededError =>
  error instanceof DesignSnapshotSupersededError;

/**
 * Settle {@link promise} when it was cancelled by a newer design edit.
 * Any other rejection is rethrown.
 */
export const ignoreDesignSnapshotSuperseded = <T>(
  promise: Promise<T> | void,
): Promise<T | undefined> => {
  if (!promise) return Promise.resolve(undefined);
  return promise.catch((error: unknown) => {
    if (isDesignSnapshotSupersededError(error)) return undefined;
    throw error;
  });
};

/**
 * Forward {@link error} to {@link report} unless it is a superseded snapshot.
 * Callers that already caught the rejection use this so Sentry and snackbars
 * stay reserved for real save failures.
 */
export const reportDesignPersistError = (
  error: unknown,
  report: (error: unknown) => void,
): void => {
  if (isDesignSnapshotSupersededError(error)) return;
  report(error);
};

export type DesignPersistSettlement = 'saved' | 'failed' | 'cancelled';

type DesignPersistOutcome =
  | { status: 'fulfilled'; value: unknown }
  | { status: 'rejected'; reason: unknown };

/**
 * Classify a design write that resolved or rejected.
 * A superseded snapshot is `cancelled`. A resolved {@link Error} is `failed`.
 * Anything else that fulfilled is `saved`.
 */
export const classifyDesignPersistSettlement = (
  outcome: DesignPersistOutcome,
): DesignPersistSettlement => {
  if (outcome.status === 'rejected') {
    return isDesignSnapshotSupersededError(outcome.reason)
      ? 'cancelled'
      : 'failed';
  }
  return guards.is.error(outcome.value) ? 'failed' : 'saved';
};

/**
 * Shape used for both {@link runSerializedDesignSnapshotPersist} `incoming`
 * and `getLiveDesign` so field compares do not spuriously diverge.
 */
export const toDesignSnapshotPersistShape = (
  design: BuildingDesign,
): BuildingDesign =>
  normalizeDesignLocationAndPoints(removeEmptyStoreys(design));

/**
 * Serialize one local design snapshot while preserving the virtual result of
 * earlier queued local snapshots. External/import changes still win when they
 * changed the same field before this snapshot executes.
 */
export const runSerializedDesignSnapshotPersist = <T>({
  projectId,
  incoming,
  getLiveDesign,
  persist,
}: SerializedDesignSnapshotPersistOptions<T>): Promise<T> => {
  const callBase = getLiveDesign();
  const queuedState = queuedDesignStateByProject.get(projectId) ?? {
    pendingCount: 0,
    tail: callBase,
  };
  const expectedLive = queuedState.tail;
  const queuedIncoming = applyChangedFields(expectedLive, callBase, incoming);
  queuedState.pendingCount += 1;
  queuedState.tail = queuedIncoming;
  queuedDesignStateByProject.set(projectId, queuedState);

  const previousTurn = designSnapshotTurn;
  let turnSettled = false;
  let resolveTurn: () => void = () => undefined;
  const turn = new Promise<void>((resolve) => {
    resolveTurn = () => {
      if (turnSettled) return;
      turnSettled = true;
      resolve();
    };
  });
  designSnapshotTurn = turn;

  // Design-save hold, not the exclusive mutex: the next design edit may enrich
  // after this one has applied locally, but import and underlay wait until this
  // save finishes (DEV-3785).
  return previousTurn
    .then(() => acquireDesignSaveHold())
    .then((releaseHold) => {
      const run = (): Promise<T> => {
        const result = rebaseQueuedLocalDesignSnapshot({
          callBase,
          expectedLive,
          incoming,
          queuedIncoming,
          live: getLiveDesign(),
        });
        switch (result.kind) {
          case 'ready':
            return persist(result.design);
          case 'superseded':
            throw new DesignSnapshotSupersededError();
          default:
            return assertUnreachable(result);
        }
      };
      let pending: Promise<T>;
      try {
        // persist applies the optimistic design before its first await.
        pending = run();
      } catch (error) {
        resolveTurn();
        releaseHold();
        return Promise.reject(error);
      }
      resolveTurn();
      return pending.finally(releaseHold);
    })
    .finally(() => {
      queuedState.pendingCount -= 1;
      if (queuedState.pendingCount > 0) return;
      if (queuedDesignStateByProject.get(projectId) !== queuedState) return;
      queuedDesignStateByProject.delete(projectId);
    });
};

/**
 * Clear virtual queued snapshots between isolated tests.
 */
export const resetSerializedDesignSnapshotPersistForTests = (): void => {
  queuedDesignStateByProject.clear();
  designSnapshotRaceAnchorByProject.clear();
  designSnapshotTurn = Promise.resolve();
};

type RebaseQueuedLocalDesignSnapshotOptions = {
  callBase: BuildingDesign;
  expectedLive: BuildingDesign;
  incoming: BuildingDesign;
  queuedIncoming: BuildingDesign;
  live: BuildingDesign;
};

type RebaseQueuedLocalDesignSnapshotResult =
  { kind: 'ready'; design: BuildingDesign } | { kind: 'superseded' };

type RebaseDesignSnapshotOnFreshOptions = {
  base: BuildingDesign;
  incoming: BuildingDesign;
  fresh: BuildingDesign;
};

type DesignSnapshotRaceAnchor = {
  updatedAt: string;
  design: BuildingDesign;
};

const designSnapshotRaceAnchorByProject = new Map<
  Project['id'],
  DesignSnapshotRaceAnchor
>();

type ResolveDesignSnapshotRaceBaseOptions = {
  projectId: Project['id'];
  updatedAt: string;
  currentDesign: BuildingDesign;
};

type AdoptDesignSnapshotRaceAnchorOptions = {
  projectId: Project['id'];
  updatedAt: string;
  design: BuildingDesign;
};

/**
 * Remember the design a successful PUT stored at `updatedAt`.
 *
 * `silentlySetUpdatedAt` stamps that timestamp onto the live project, which
 * may already contain a newer unsaved edit. The next save must rebase against
 * the design that was saved, not that live project (DEV-3857).
 */
export const adoptDesignSnapshotRaceAnchor = ({
  projectId,
  updatedAt,
  design,
}: AdoptDesignSnapshotRaceAnchorOptions): void => {
  designSnapshotRaceAnchorByProject.set(projectId, { updatedAt, design });
};

/**
 * Design that matches the `updated_at` this snapshot will send.
 *
 * Overlapping design saves keep that timestamp until a PUT lands. Diffing a
 * later save against the previous local snapshot makes the server copy look
 * like a conflicting `buildings` edit, and replay resets (DEV-3857).
 *
 * A timestamp this client has not saved yet uses {@link currentDesign}. After
 * {@link adoptDesignSnapshotRaceAnchor}, that timestamp keeps the saved design
 * even when the live project has moved on.
 */
export const resolveDesignSnapshotRaceBase = ({
  projectId,
  updatedAt,
  currentDesign,
}: ResolveDesignSnapshotRaceBaseOptions): BuildingDesign => {
  const anchor = designSnapshotRaceAnchorByProject.get(projectId);
  if (anchor?.updatedAt === updatedAt) return anchor.design;
  designSnapshotRaceAnchorByProject.set(projectId, {
    updatedAt,
    design: currentDesign,
  });
  return currentDesign;
};

type Merged<T> = { kind: 'value'; value: T } | { kind: 'conflict' };

type VersionPresence =
  | 'none'
  | 'base'
  | 'incoming'
  | 'fresh'
  | 'base-incoming'
  | 'base-fresh'
  | 'incoming-fresh'
  | 'all';

/**
 * Which of base, incoming, and fresh contain this id.
 */
const versionPresence = (
  hasBase: boolean,
  hasIncoming: boolean,
  hasFresh: boolean,
): VersionPresence => {
  if (hasBase && hasIncoming && hasFresh) return 'all';
  if (hasBase && hasIncoming) return 'base-incoming';
  if (hasBase && hasFresh) return 'base-fresh';
  if (hasIncoming && hasFresh) return 'incoming-fresh';
  if (hasBase) return 'base';
  if (hasIncoming) return 'incoming';
  if (hasFresh) return 'fresh';
  return 'none';
};

/**
 * Keep fresh when this snapshot did not change the value. Otherwise take
 * incoming when the server still matches base or already matches incoming.
 */
const mergeValue = <T>(base: T, incoming: T, fresh: T): Merged<T> => {
  if (isEqual(incoming, base)) return { kind: 'value', value: fresh };
  if (isEqual(fresh, base) || isEqual(fresh, incoming)) {
    return { kind: 'value', value: incoming };
  }
  return { kind: 'conflict' };
};

const ZERO_SHIFT: Point2D = [0, 0];

/**
 * Deltas closer than this are the same footprint translation.
 * A dragged vertex is metres away, not a float leftover.
 */
const ORIGIN_SHIFT_EPSILON_METERS = 1e-4;

/**
 * True when {@link delta} does not move geometry.
 */
const isZeroShift = (delta: Point2D): boolean =>
  delta[0] === 0 && delta[1] === 0;

/**
 * True when two footprint translations are the same offset.
 */
const shiftsMatch = (left: Point2D, right: Point2D): boolean =>
  Math.abs(left[0] - right[0]) <= ORIGIN_SHIFT_EPSILON_METERS &&
  Math.abs(left[1] - right[1]) <= ORIGIN_SHIFT_EPSILON_METERS;

/**
 * Translation shared by every shape the user did not edit.
 *
 * {@link toDesignSnapshotPersistShape} recenters after a storey removal, so
 * every surviving shape shifts by the same offset. A second uniform delta is
 * a real edit (a dragged outline can have more points than the recenter).
 * Majority voting would treat that drag as the origin and drop it. Mixed
 * deltas therefore yield a zero shift so the compare still conflicts.
 */
const originShiftFromDesigns = (
  base: BuildingDesign,
  incoming: BuildingDesign,
): Point2D => {
  const baseBuildings = new Map(
    base.buildings.map((building) => [building.id, building]),
  );
  const deltas: Point2D[] = [];
  for (const incomingBuilding of incoming.buildings) {
    const baseBuilding = baseBuildings.get(incomingBuilding.id);
    if (!baseBuilding) continue;
    const baseStoreys = new Map(
      baseBuilding.storeys.map((storey) => [storey.id, storey]),
    );
    for (const incomingStorey of incomingBuilding.storeys) {
      const baseStorey = baseStoreys.get(incomingStorey.id);
      if (!baseStorey) continue;
      collectUniformShapeShifts(baseStorey, incomingStorey, deltas);
    }
  }
  const first = deltas[0];
  if (!first) return ZERO_SHIFT;
  if (deltas.some((delta) => !shiftsMatch(delta, first))) return ZERO_SHIFT;
  return first;
};

/**
 * Record one shape when every point moved by the same offset.
 */
const collectUniformShapeShifts = (
  baseStorey: Storey,
  incomingStorey: Storey,
  deltas: Point2D[],
): void => {
  const baseShapes = new Map(
    baseStorey.shapes.map((shape) => [shape.id, shape]),
  );
  for (const incomingShape of incomingStorey.shapes) {
    const baseShape = baseShapes.get(incomingShape.id);
    if (!baseShape) continue;
    if (baseShape.points.length !== incomingShape.points.length) continue;
    const firstBase = baseShape.points[0];
    const firstIncoming = incomingShape.points[0];
    if (!firstBase || !firstIncoming) continue;
    const delta: Point2D = [
      firstIncoming[0] - firstBase[0],
      firstIncoming[1] - firstBase[1],
    ];
    const uniform = baseShape.points.every((point, index) => {
      const next = incomingShape.points[index];
      if (!next) return false;
      return (
        Math.abs(next[0] - point[0] - delta[0]) <=
          ORIGIN_SHIFT_EPSILON_METERS &&
        Math.abs(next[1] - point[1] - delta[1]) <= ORIGIN_SHIFT_EPSILON_METERS
      );
    });
    if (!uniform) continue;
    deltas.push(delta);
  }
};

type OriginFrames = {
  /** Point delta of incoming relative to base. */
  client: Point2D;
  /** Added to fresh points so they share incoming's origin. */
  freshToIncoming: Point2D;
};

/**
 * Move a map pin opposite a footprint translation.
 *
 * {@link normalizeDesignLocationAndPoints} does this so the buildings stay
 * on the map when local points are recentered.
 */
const shiftLocationOppositePoints = (
  location: Coordinate,
  pointDelta: Point2D,
): Coordinate => {
  if (isZeroShift(pointDelta)) return location;
  const east = -pointDelta[0];
  const north = -pointDelta[1];
  const afterEast = moveCoordinate(
    location,
    [Math.abs(east), 'meters'],
    [east >= 0 ? 90 : 270, 'degrees'],
  );
  return moveCoordinate(
    afterEast,
    [Math.abs(north), 'meters'],
    [north >= 0 ? 180 : 0, 'degrees'],
  );
};

/**
 * Move storey footprints by {@link delta}. A zero delta keeps the same storey.
 */
const translateStoreyPoints = (storey: Storey, delta: Point2D): Storey => {
  if (isZeroShift(delta)) return storey;
  return mutateStorey.all.points.translate(storey, delta);
};

/**
 * Move plan underlays by the same offset as footprints.
 */
const translateUnderlays = (
  underlays: BlueprintUnderlay[] | undefined,
  delta: Point2D,
): BlueprintUnderlay[] | undefined => {
  if (!underlays || isZeroShift(delta)) return underlays;
  return underlays.map((underlay) =>
    translateBlueprintUnderlay(underlay, delta),
  );
};

type BuildingFrameShift = {
  /**
   * `keep` for a building that already existed on the base. Persist recenter
   * does not move its underlays.
   * `move` for a building that exists only on fresh, where the sheet was
   * drawn on the server-frame walls and must follow them.
   */
  underlays: 'keep' | 'move';
};

/**
 * Move every storey in {@link building} into another origin frame.
 * Building underlays move only when {@link BuildingFrameShift.underlays} is
 * `move`.
 */
const translateBuildingPoints = (
  building: Building,
  delta: Point2D,
  options: BuildingFrameShift,
): Building => {
  if (isZeroShift(delta)) return building;
  const storeys = building.storeys.map((storey) =>
    translateStoreyPoints(storey, delta),
  );
  switch (options.underlays) {
    case 'keep':
      return { ...building, storeys };
    case 'move': {
      const blueprintUnderlays = translateUnderlays(
        building.blueprintUnderlays,
        delta,
      );
      return {
        ...building,
        storeys,
        ...(blueprintUnderlays ? { blueprintUnderlays } : {}),
      };
    }
    default:
      return assertUnreachable(options.underlays);
  }
};

/**
 * Compare {@link incoming} after undoing the origin shift. A pure recenter
 * keeps {@link fresh} and reapplies that shift into the incoming frame. A real
 * edit still conflicts when the server changed the same value.
 */
const mergeShifted = <T>(
  base: T,
  incoming: T,
  fresh: T,
  align: (value: T) => T,
  reapply: (value: T) => T,
): Merged<T> => {
  if (isEqual(incoming, base)) return { kind: 'value', value: fresh };
  const incomingAligned = align(incoming);
  if (isEqual(incomingAligned, base)) {
    return { kind: 'value', value: reapply(fresh) };
  }
  if (
    isEqual(fresh, base) ||
    isEqual(fresh, incoming) ||
    isEqual(fresh, incomingAligned)
  ) {
    return { kind: 'value', value: incoming };
  }
  return { kind: 'conflict' };
};

/**
 * Order kept items from the side that changed membership, then append the
 * other side's new ids.
 *
 * Storeys are added at the end of the list, so an appended server storey
 * stays above the floors this snapshot kept. A storey inserted at another
 * index is not represented and would move, including the ground floor.
 */
const orderKeptById = <T extends { id: string }>(
  base: readonly T[],
  incoming: readonly T[],
  fresh: readonly T[],
  kept: ReadonlyMap<string, T>,
): T[] => {
  const userChangedIds = !isEqual(
    incoming.map((item) => item.id),
    base.map((item) => item.id),
  );
  const seeds = userChangedIds
    ? [incoming, fresh, base]
    : [fresh, incoming, base];
  const ordered: T[] = [];
  const seen = new Set<string>();
  for (const list of seeds) {
    for (const item of list) {
      if (seen.has(item.id)) continue;
      const next = kept.get(item.id);
      if (!next) continue;
      seen.add(item.id);
      ordered.push(next);
    }
  }
  return ordered;
};

/**
 * Merge one storey. A storey removed by this snapshot stays removed. A
 * storey only the server has is kept, in the incoming origin frame. Both
 * sides editing one surviving storey is a conflict. A footprint recenter is
 * not an edit.
 */
const mergeStoreyVersion = (
  base: Storey | undefined,
  incoming: Storey | undefined,
  fresh: Storey | undefined,
  frames: OriginFrames,
): Merged<Storey | undefined> => {
  const undoClient: Point2D = [-frames.client[0], -frames.client[1]];
  const presence = versionPresence(
    base !== undefined,
    incoming !== undefined,
    fresh !== undefined,
  );
  switch (presence) {
    case 'none':
    case 'base':
    case 'base-fresh':
      return { kind: 'value', value: undefined };
    case 'fresh':
      return fresh
        ? {
            kind: 'value',
            value: translateStoreyPoints(fresh, frames.freshToIncoming),
          }
        : { kind: 'conflict' };
    case 'incoming':
      return incoming
        ? { kind: 'value', value: incoming }
        : { kind: 'conflict' };
    case 'incoming-fresh': {
      if (!incoming || !fresh) return { kind: 'conflict' };
      const freshShifted = translateStoreyPoints(fresh, frames.freshToIncoming);
      if (isEqual(incoming, fresh) || isEqual(incoming, freshShifted)) {
        return { kind: 'value', value: incoming };
      }
      return { kind: 'conflict' };
    }
    case 'base-incoming': {
      if (!base || !incoming) return { kind: 'conflict' };
      const aligned = translateStoreyPoints(incoming, undoClient);
      if (isEqual(incoming, base) || isEqual(aligned, base)) {
        return { kind: 'value', value: undefined };
      }
      return { kind: 'conflict' };
    }
    case 'all': {
      if (!base || !incoming || !fresh) return { kind: 'conflict' };
      return mergeShifted(
        base,
        incoming,
        fresh,
        (storey) => translateStoreyPoints(storey, undoClient),
        (storey) => translateStoreyPoints(storey, frames.freshToIncoming),
      );
    }
    default:
      return assertUnreachable(presence);
  }
};

/**
 * Merge storeys by id so a removal replays beside an unrelated server edit.
 */
const mergeStoreys = (
  base: readonly Storey[],
  incoming: readonly Storey[],
  fresh: readonly Storey[],
  frames: OriginFrames,
): Merged<Storey[]> => {
  const baseById = new Map(base.map((storey) => [storey.id, storey]));
  const incomingById = new Map(incoming.map((storey) => [storey.id, storey]));
  const freshById = new Map(fresh.map((storey) => [storey.id, storey]));
  const ids = [
    ...base.map((storey) => storey.id),
    ...incoming.map((storey) => storey.id),
    ...fresh.map((storey) => storey.id),
  ];
  const kept = new Map<string, Storey>();
  for (const id of ids) {
    if (kept.has(id)) continue;
    const merged = mergeStoreyVersion(
      baseById.get(id),
      incomingById.get(id),
      freshById.get(id),
      frames,
    );
    switch (merged.kind) {
      case 'conflict':
        return { kind: 'conflict' };
      case 'value':
        if (merged.value) kept.set(id, merged.value);
        break;
      default:
        return assertUnreachable(merged);
    }
  }
  return {
    kind: 'value',
    value: orderKeptById(base, incoming, fresh, kept),
  };
};

/**
 * Merge one building. Storeys merge by id; name and underlays stay atomic.
 * Underlays are compared with the footprint shift undone.
 */
const mergeBuildingVersion = (
  base: Building,
  incoming: Building,
  fresh: Building,
  frames: OriginFrames,
): Merged<Building> => {
  const undoClient: Point2D = [-frames.client[0], -frames.client[1]];
  const storeys = mergeStoreys(
    base.storeys,
    incoming.storeys,
    fresh.storeys,
    frames,
  );
  switch (storeys.kind) {
    case 'conflict':
      return { kind: 'conflict' };
    case 'value':
      break;
    default:
      return assertUnreachable(storeys);
  }
  const name = mergeValue(base.name, incoming.name, fresh.name);
  switch (name.kind) {
    case 'conflict':
      return { kind: 'conflict' };
    case 'value':
      break;
    default:
      return assertUnreachable(name);
  }
  const blueprintUnderlays = mergeShifted(
    base.blueprintUnderlays,
    incoming.blueprintUnderlays,
    fresh.blueprintUnderlays,
    (underlays) => translateUnderlays(underlays, undoClient),
    (underlays) => translateUnderlays(underlays, frames.freshToIncoming),
  );
  switch (blueprintUnderlays.kind) {
    case 'conflict':
      return { kind: 'conflict' };
    case 'value':
      break;
    default:
      return assertUnreachable(blueprintUnderlays);
  }
  // Drop fresh name/underlays first so a cleared value does not survive the spread.
  const rest = omit(fresh, 'name', 'blueprintUnderlays');
  return {
    kind: 'value',
    value: {
      ...rest,
      ...omitUndefined({
        name: name.value,
        blueprintUnderlays: blueprintUnderlays.value,
      }),
      storeys: storeys.value,
    },
  };
};

/**
 * Merge buildings by id. Removing a building or storey wins over a server
 * edit to that same record. An edit both sides made to one surviving record
 * does not replay.
 */
const mergeBuildings = (
  base: readonly Building[],
  incoming: readonly Building[],
  fresh: readonly Building[],
  frames: OriginFrames,
): Merged<Building[]> => {
  const undoClient: Point2D = [-frames.client[0], -frames.client[1]];
  const baseById = new Map(base.map((building) => [building.id, building]));
  const incomingById = new Map(
    incoming.map((building) => [building.id, building]),
  );
  const freshById = new Map(fresh.map((building) => [building.id, building]));
  const ids = [
    ...base.map((building) => building.id),
    ...incoming.map((building) => building.id),
    ...fresh.map((building) => building.id),
  ];
  const kept = new Map<string, Building>();
  for (const id of ids) {
    if (kept.has(id)) continue;
    const baseBuilding = baseById.get(id);
    const incomingBuilding = incomingById.get(id);
    const freshBuilding = freshById.get(id);
    const presence = versionPresence(
      baseBuilding !== undefined,
      incomingBuilding !== undefined,
      freshBuilding !== undefined,
    );
    switch (presence) {
      case 'none':
      case 'base':
      case 'base-fresh':
        break;
      case 'fresh':
        if (freshBuilding) {
          kept.set(
            id,
            translateBuildingPoints(freshBuilding, frames.freshToIncoming, {
              underlays: 'move',
            }),
          );
        }
        break;
      case 'incoming':
        if (incomingBuilding) kept.set(id, incomingBuilding);
        break;
      case 'incoming-fresh': {
        if (!incomingBuilding || !freshBuilding) return { kind: 'conflict' };
        const freshShifted = translateBuildingPoints(
          freshBuilding,
          frames.freshToIncoming,
          { underlays: 'move' },
        );
        if (
          !isEqual(incomingBuilding, freshBuilding) &&
          !isEqual(incomingBuilding, freshShifted)
        ) {
          return { kind: 'conflict' };
        }
        kept.set(id, incomingBuilding);
        break;
      }
      case 'base-incoming': {
        if (!baseBuilding || !incomingBuilding) return { kind: 'conflict' };
        const aligned = translateBuildingPoints(incomingBuilding, undoClient, {
          underlays: 'keep',
        });
        if (
          !isEqual(incomingBuilding, baseBuilding) &&
          !isEqual(aligned, baseBuilding)
        ) {
          return { kind: 'conflict' };
        }
        break;
      }
      case 'all': {
        if (!baseBuilding || !incomingBuilding || !freshBuilding) {
          return { kind: 'conflict' };
        }
        const merged = mergeBuildingVersion(
          baseBuilding,
          incomingBuilding,
          freshBuilding,
          frames,
        );
        switch (merged.kind) {
          case 'conflict':
            return { kind: 'conflict' };
          case 'value':
            kept.set(id, merged.value);
            break;
          default:
            return assertUnreachable(merged);
        }
        break;
      }
      default:
        return assertUnreachable(presence);
    }
  }
  return {
    kind: 'value',
    value: orderKeptById(base, incoming, fresh, kept),
  };
};

/**
 * Keep a server pin move when this snapshot only recentered the footprint.
 * A real location edit still conflicts when the server pin differs.
 */
const mergeLocation = (
  base: Coordinate,
  incoming: Coordinate,
  fresh: Coordinate,
  frames: OriginFrames,
): Merged<Coordinate> => {
  if (isEqual(incoming, base)) return { kind: 'value', value: fresh };
  if (isEqual(fresh, incoming)) return { kind: 'value', value: incoming };
  const recentered = shiftLocationOppositePoints(base, frames.client);
  if (isEqual(incoming, recentered)) {
    return {
      kind: 'value',
      value: shiftLocationOppositePoints(fresh, frames.freshToIncoming),
    };
  }
  if (!isEqual(fresh, base)) return { kind: 'conflict' };
  return { kind: 'value', value: incoming };
};

/**
 * Reapply a failed design snapshot onto freshly fetched server state.
 *
 * Only fields changed by this snapshot are copied. `buildings` merges by id
 * so a storey removal replays beside an unrelated server edit to another
 * storey. A footprint recenter from that removal is undone before storeys
 * and design underlays are compared with {@link base}. Fresh geometry is in
 * the server origin, so the server shift is removed and the client shift
 * applied. The pin moves with that same correction instead of conflicting.
 * Building underlays are not part of the recenter. If the server changed one
 * of the same non-building fields, or the same surviving storey, the edit is
 * a real conflict and must not be replayed.
 */
export const rebaseDesignSnapshotOnFresh = ({
  base,
  incoming,
  fresh,
}: RebaseDesignSnapshotOnFreshOptions): BuildingDesign | undefined => {
  const client = originShiftFromDesigns(base, incoming);
  const server = originShiftFromDesigns(base, fresh);
  const frames: OriginFrames = {
    client,
    freshToIncoming: [client[0] - server[0], client[1] - server[1]],
  };
  const undoClient: Point2D = [-client[0], -client[1]];
  const next: BuildingDesign = { ...fresh };
  for (const key of PERSISTED_DESIGN_FIELDS) {
    if (key === 'location') {
      const merged = mergeLocation(
        base.location,
        incoming.location,
        fresh.location,
        frames,
      );
      switch (merged.kind) {
        case 'value':
          next.location = merged.value;
          continue;
        case 'conflict':
          return undefined;
        default:
          return assertUnreachable(merged);
      }
    }
    if (isEqual(incoming[key], base[key])) continue;
    if (isEqual(fresh[key], incoming[key])) continue;
    if (key === 'buildings') {
      const merged = mergeBuildings(
        base.buildings,
        incoming.buildings,
        fresh.buildings,
        frames,
      );
      switch (merged.kind) {
        case 'value':
          next.buildings = merged.value;
          continue;
        case 'conflict':
          return undefined;
        default:
          return assertUnreachable(merged);
      }
    }
    if (key === 'blueprintUnderlays') {
      const merged = mergeShifted(
        base.blueprintUnderlays,
        incoming.blueprintUnderlays,
        fresh.blueprintUnderlays,
        (underlays) => translateUnderlays(underlays, undoClient),
        (underlays) => translateUnderlays(underlays, frames.freshToIncoming),
      );
      switch (merged.kind) {
        case 'value':
          next.blueprintUnderlays = merged.value;
          continue;
        case 'conflict':
          return undefined;
        default:
          return assertUnreachable(merged);
      }
    }
    if (!isEqual(fresh[key], base[key])) return undefined;
    copyDesignField(next, key, incoming);
  }
  return next;
};

/**
 * Apply the current call's changed fields onto live state.
 *
 * If live matches the virtual predecessor, keep the complete local queue.
 * If a predecessor failed and live still matches this call's original base,
 * apply only this call. Otherwise cancel rather than overwriting an external
 * change to the same field.
 */
const rebaseQueuedLocalDesignSnapshot = ({
  callBase,
  expectedLive,
  incoming,
  queuedIncoming,
  live,
}: RebaseQueuedLocalDesignSnapshotOptions): RebaseQueuedLocalDesignSnapshotResult => {
  const next: BuildingDesign = { ...live };
  for (const key of PERSISTED_DESIGN_FIELDS) {
    if (isEqual(incoming[key], callBase[key])) continue;
    if (isEqual(live[key], expectedLive[key])) {
      copyDesignField(next, key, queuedIncoming);
      continue;
    }
    if (isEqual(live[key], callBase[key])) {
      copyDesignField(next, key, incoming);
      continue;
    }
    return { kind: 'superseded' };
  }
  return { kind: 'ready', design: next };
};

/**
 * Apply fields changed between {@link base} and {@link incoming} onto
 * {@link target}, allowing a later local call to supersede an earlier one.
 */
const applyChangedFields = (
  target: BuildingDesign,
  base: BuildingDesign,
  incoming: BuildingDesign,
): BuildingDesign => {
  const next: BuildingDesign = { ...target };
  for (const key of PERSISTED_DESIGN_FIELDS) {
    if (isEqual(incoming[key], base[key])) continue;
    copyDesignField(next, key, incoming);
  }
  return next;
};

/**
 * Copy one persisted field while retaining its key-specific value type.
 */
const copyDesignField = <K extends (typeof PERSISTED_DESIGN_FIELDS)[number]>(
  target: BuildingDesign,
  key: K,
  source: BuildingDesign,
): void => {
  target[key] = source[key];
};
